import React from 'react';
import { useTranslation } from 'react-i18next';
import { Scale, Ruler, Box, Thermometer, Clock, Zap, ArrowLeft } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useLocalizedPath } from '../hooks/useLocalizedPath';
import SEOHead from './SEOHead';
import LocalizedLink from './LocalizedLink';

const CommonUnitSystemsPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { getLocalizedPath } = useLocalizedPath();

  const unitSystems = [
    {
      id: 'si',
      icon: Scale,
      color: 'from-blue-500 to-blue-600'
    },
    {
      id: 'metric',
      icon: Ruler,
      color: 'from-green-500 to-green-600'
    },
    {
      id: 'usCustomary',
      icon: Box,
      color: 'from-red-500 to-red-600'
    },
    {
      id: 'imperial',
      icon: Scale,
      color: 'from-purple-500 to-purple-600'
    },
    {
      id: 'cgs',
      icon: Zap,
      color: 'from-orange-500 to-orange-600'
    },
    {
      id: 'mks',
      icon: Ruler,
      color: 'from-teal-500 to-teal-600'
    },
    {
      id: 'fps',
      icon: Clock,
      color: 'from-indigo-500 to-indigo-600'
    },
    {
      id: 'natural',
      icon: Thermometer,
      color: 'from-pink-500 to-pink-600'
    }
  ];

  const handleBackToHome = () => {
    navigate(getLocalizedPath('/'));
  };

  return (
    <div className="space-y-6">
      <SEOHead
        title={t('unitSystems.pageTitle')}
        description={t('unitSystems.pageDescription')}
      />

      {/* Back Button */}
      <button
        onClick={handleBackToHome}
        className="flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors"
      >
        <ArrowLeft className="h-4 w-4" />
        {t('common.backToAllConverters')}
      </button>

      {/* Header */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        <div className="bg-gradient-to-r from-[#4a7c9b] to-[#5a8caa] px-4 py-4">
          <h1 className="text-xl font-semibold text-white">
            {t('unitSystems.pageTitle')}
          </h1>
        </div>
        <div className="p-4">
          <p className="text-gray-600">
            {t('unitSystems.introduction')}
          </p>
        </div>
      </div>

      {/* Unit Systems Grid */}
      <div className="grid gap-6 md:grid-cols-2">
        {unitSystems.map((system) => {
          const IconComponent = system.icon;
          return (
            <div
              key={system.id}
              className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden"
            >
              <div className={`bg-gradient-to-r ${system.color} px-4 py-3`}>
                <div className="flex items-center gap-2">
                  <IconComponent className="h-5 w-5 text-white" />
                  <h2 className="text-lg font-semibold text-white">
                    {t(`unitSystems.systems.${system.id}.name`)}
                  </h2>
                </div>
              </div>
              <div className="p-4">
                <p className="text-gray-600 text-sm mb-3">
                  {t(`unitSystems.systems.${system.id}.description`)}
                </p>
                <div className="space-y-2">
                  <h3 className="font-medium text-gray-700 text-sm">
                    {t('unitSystems.baseUnits')}:
                  </h3>
                  <ul className="text-sm text-gray-600 space-y-1">
                    {t(`unitSystems.systems.${system.id}.units`, { returnObjects: true }).map((unit, index) => (
                      <li key={index} className="flex items-center gap-2">
                        <span className="w-1.5 h-1.5 rounded-full bg-gray-400"></span>
                        {unit}
                      </li>
                    ))}
                  </ul>
                </div>
                {t(`unitSystems.systems.${system.id}.usage`) && (
                  <div className="mt-3 pt-3 border-t border-gray-100">
                    <p className="text-xs text-gray-500">
                      <span className="font-medium">{t('unitSystems.usage')}:</span> {t(`unitSystems.systems.${system.id}.usage`)}
                    </p>
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Comparison Table */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        <div className="bg-gradient-to-r from-gray-600 to-gray-700 px-4 py-3">
          <h2 className="text-lg font-semibold text-white">
            {t('unitSystems.comparisonTitle')}
          </h2>
        </div>
        <div className="p-4 overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-200">
                <th className="text-left py-2 px-3 font-medium text-gray-700">
                  {t('unitSystems.table.quantity')}
                </th>
                <th className="text-left py-2 px-3 font-medium text-gray-700">
                  {t('unitSystems.table.si')}
                </th>
                <th className="text-left py-2 px-3 font-medium text-gray-700">
                  {t('unitSystems.table.usCustomary')}
                </th>
                <th className="text-left py-2 px-3 font-medium text-gray-700">
                  {t('unitSystems.table.imperial')}
                </th>
              </tr>
            </thead>
            <tbody>
              {t('unitSystems.comparisonTable', { returnObjects: true }).map((row, index) => (
                <tr key={index} className="border-b border-gray-100 hover:bg-gray-50">
                  <td className="py-2 px-3 font-medium text-gray-700">{row.quantity}</td>
                  <td className="py-2 px-3 text-gray-600">{row.si}</td>
                  <td className="py-2 px-3 text-gray-600">{row.usCustomary}</td>
                  <td className="py-2 px-3 text-gray-600">{row.imperial}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Additional Information */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        <div className="bg-gradient-to-r from-[#4a7c9b] to-[#5a8caa] px-4 py-3">
          <h2 className="text-lg font-semibold text-white">
            {t('unitSystems.historyTitle')}
          </h2>
        </div>
        <div className="p-4">
          <p className="text-gray-600 text-sm">
            {t('unitSystems.historyContent')}
          </p>
        </div>
      </div>

      {/* Quick Links to Converters */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        <div className="bg-gradient-to-r from-[#4a7c9b] to-[#5a8caa] px-4 py-3">
          <h2 className="text-lg font-semibold text-white">
            {t('unitSystems.relatedConverters')}
          </h2>
        </div>
        <div className="p-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <LocalizedLink
              to="/convert/length"
              className="flex items-center gap-2 px-3 py-2 bg-blue-50 text-blue-700 rounded-lg hover:bg-blue-100 transition-colors text-sm"
            >
              <Ruler className="h-4 w-4" />
              {t('converters.length.name')}
            </LocalizedLink>
            <LocalizedLink
              to="/convert/weight"
              className="flex items-center gap-2 px-3 py-2 bg-green-50 text-green-700 rounded-lg hover:bg-green-100 transition-colors text-sm"
            >
              <Scale className="h-4 w-4" />
              {t('converters.weight.name')}
            </LocalizedLink>
            <LocalizedLink
              to="/convert/volume"
              className="flex items-center gap-2 px-3 py-2 bg-purple-50 text-purple-700 rounded-lg hover:bg-purple-100 transition-colors text-sm"
            >
              <Box className="h-4 w-4" />
              {t('converters.volume.name')}
            </LocalizedLink>
            <LocalizedLink
              to="/convert/temperature"
              className="flex items-center gap-2 px-3 py-2 bg-orange-50 text-orange-700 rounded-lg hover:bg-orange-100 transition-colors text-sm"
            >
              <Thermometer className="h-4 w-4" />
              {t('converters.temperature.name')}
            </LocalizedLink>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CommonUnitSystemsPage;
